/* global firebase */
import { useState, useEffect } from 'react';
import { createCompany, addUserToCompany } from './companies';
import { createUser } from './users';
import plans from 'config/plans';



/**
 * A hook to watch the current session. Returns `null` while loading, or
 * fields for:
 *
 * - uid
 * - email
 * - displayName
 * - emailVerified
 * - user
 * - company
 *
 */
export function useAuth() {
  const [auth, setAuth] = useState(firebase.auth.currentUser || null);
  const [user, setUser] = useState(null);
  const [company, setCompany] = useState(null);

  useEffect(() => {
    return firebase.auth().onAuthStateChanged(res => {
      setAuth(res || {});
    });
  }, []);

  useEffect(() => {
    if(auth === null) {
      setUser(null);
      return;
    }
    if(!auth.uid) {
      setUser(false);
      return;
    }
    return firebase.firestore().collection('users').doc(auth.uid)
      .onSnapshot(doc => {
        if(doc.exists) {
          setUser({...doc.data(), id:auth.uid});
        }
        else {
          setUser({});
        }
      });
  }, [auth]);

  useEffect(() => {
    if(user === null) {
      setCompany(null);
      return;
    }
    if(!user.companyId) {
      setCompany(false);
      return;
    }
    return firebase.firestore().collection('companies').doc(user.companyId)
      .onSnapshot(doc => {
        if(doc.exists) {
          setCompany({...doc.data(), id:user.companyId});
        }
        else {
          setCompany({});
        }
      });
  }, [user]);

  if(auth === null || user === null || company === null) {
    return null;
  }

  return {
    uid: auth.uid,
    email: auth.email,
    displayName: user.displayName || "User",
    emailVerified: auth.emailVerified,
    user,
    company,
  };
}


/**
 * Log a user in.
 *
 * @prop {String} email
 * @prop {String} password
 */
export function login(email, password) {
  return firebase.auth().signInWithEmailAndPassword(email, password);
}


/**
 * Log the user out.
 */
export function logout() {
  return firebase.auth().signOut();
}


/**
 * Register a new user. This:
 * - Creates the user with email and password,
 * - sends a verification email,
 * - creates a new company, and
 * - adds that companyId to the new user.
 *
 * @prop {Object} data
 * @prop {String} data.displayName
 * @prop {String} data.email
 * @prop {String} data.password
 * @prop {Object} data.plan
 */
export async function register(data, inviteId) {
  
  // If an invitation exists, load it's data and
  // remove it. 
  let invite;
  if(inviteId) {
    invite = await firebase.firestore()
      .collection('invites')
      .doc(inviteId)
      .get()
      .then(doc => {
        if(!doc.exists) {
          return null;
        }
        return doc.data();
      });
    
    if(!invite) {
      throw new Error('invite-not-found');
    }
    
    firebase.firestore()
      .collection('invites')
      .doc(inviteId)
      .delete();
  }
  
  
  // Create the user.
  
  await firebase.auth().createUserWithEmailAndPassword(data.email, data.password);
  const user = firebase.auth().currentUser;
  await sendEmailVerification(data.email);

  const userData = await createUser(user.uid, {
    displayName: data.displayName,
    email: data.email,
    dateCreated: Date.now(),
    role: inviteId ? "CONTRIBUTOR" : "COMPANY_ADMIN",
  });
  
  // Create the company.
  let companyId;
  if(invite && invite.companyId) {
    companyId = invite.companyId;
  }
  else {
    let plan = null;
    if(data.planId) {
      plan = plans.find(p => p.monthlyId === data.planId || p.yearlyId === data.planId);
      plan.id = data.planId;
    }
    const company = await createCompany({email:data.email, plan:plan});
    companyId = company.id;
  }
  
  await addUserToCompany(userData.id, companyId);
}


/**
 * Send email verification.
 */
export const sendEmailVerification = firebase.functions().httpsCallable('sendEmailVerification');


/**
 * Verify an email address.
 */
export function verifyEmail(code) {
  return firebase.auth().applyActionCode(code);
}


/**
 * Reset user password.
 *
 * @prop {String} email
 */
export const sendPasswordReset = firebase.functions().httpsCallable('sendPasswordReset');


/**
 * Change user password
 *
 * @prop {String} password
 * The new password.
 *
 * @prop {Object} params
 * Query params passed through from firebase.
 */
export async function resetPassword(password, params) {
  const email = await firebase.auth().verifyPasswordResetCode(params.oobCode);
  await firebase.auth().confirmPasswordReset(params.oobCode, password);
  return login(email, password);
}


/**
 * Change the current user's password.
 * 
 * @param {String} password
 */
export function changePassword(password) {
  var user = firebase.auth().currentUser;
  return user.updatePassword(password);
}